import { createFileRoute, Link } from "@tanstack/solid-router";
import { ErrorBoundary, Suspense } from "solid-js";
import { ProjectCard } from "~/components/projects/ProjectCard";
import { ProjectsSkeleton } from "~/components/projects/ProjectsSkeleton";
import { StatCard } from "~/components/projects/StatCard";
import { ErrorCard } from "~/components/ui/ErrorCard";
import { useAuth } from "~/lib/auth";
import { useProjectsQuery } from "~/lib/queries/projects";

export const Route = createFileRoute("/_authenticated/dashboard")({
  ssr: false,
  component: DashboardPage,
});

function DashboardContent() {
  const auth = useAuth();
  const projectsQuery = useProjectsQuery();

  const projects = () => projectsQuery.data ?? [];
  const totalProjects = () => projects().length;

  const recentProjects = () => projects().slice(0, 6);

  const userEmail = () =>
    auth.user()?.profile?.email ?? auth.user()?.profile?.sub ?? "there";
  const firstName = () => {
    const email = userEmail();
    return email.includes("@") ? email.split("@")[0] : email;
  };

  return (
    <div class="max-w-6xl mx-auto space-y-8">
      {/* Welcome banner */}
      <div class="rounded-xl border border-border bg-card px-6 py-5 flex items-center justify-between gap-4">
        <div>
          <h1 class="text-xl font-semibold text-foreground">
            Welcome back, <span class="text-primary">{firstName()}</span>
          </h1>
          <p class="text-sm text-muted-foreground mt-0.5">
            Here's an overview of your projects.
          </p>
        </div>
        <Link
          to="/projects/new"
          mask={{ to: "/projects", unmaskOnReload: true }}
          class="inline-flex h-9 items-center gap-2 px-4 rounded-lg bg-primary text-primary-foreground
                 text-sm font-medium hover:bg-primary/90 transition-colors shrink-0
                 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span aria-hidden="true">+</span>
          New Project
        </Link>
      </div>

      {/* Stat cards */}
      <div class="max-w-sm">
        <StatCard
          label="Total Projects"
          value={totalProjects()}
          accent
          icon={
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              aria-hidden="true"
            >
              <rect
                x="2"
                y="2"
                width="6"
                height="6"
                rx="1.5"
                fill="currentColor"
              />
              <rect
                x="10"
                y="2"
                width="6"
                height="6"
                rx="1.5"
                fill="currentColor"
                opacity="0.5"
              />
              <rect
                x="2"
                y="10"
                width="6"
                height="6"
                rx="1.5"
                fill="currentColor"
                opacity="0.5"
              />
              <rect
                x="10"
                y="10"
                width="6"
                height="6"
                rx="1.5"
                fill="currentColor"
                opacity="0.3"
              />
            </svg>
          }
        />
      </div>

      {/* Recent projects */}
      <div>
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-sm font-semibold text-foreground">Recent Projects</h2>
          <Link
            to="/projects"
            class="text-xs text-primary hover:text-primary/80 transition-colors font-medium"
          >
            View all →
          </Link>
        </div>

        {projectsQuery.isPending && <ProjectsSkeleton />}

        {projectsQuery.isError && (
          <ErrorCard
            message="Failed to load projects. Please try again."
            action={
              <button
                type="button"
                onClick={() => projectsQuery.refetch()}
                class="inline-flex h-8 items-center px-3 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
              >
                Retry
              </button>
            }
          />
        )}

        {projectsQuery.isSuccess && recentProjects().length === 0 && (
          <div class="rounded-xl border border-dashed border-border p-10 text-center">
            <p class="text-sm text-muted-foreground mb-4">
              No projects yet. Create your first one!
            </p>
            <Link
              to="/projects/new"
              mask={{ to: "/projects", unmaskOnReload: true }}
              class="inline-flex h-9 items-center gap-2 px-4 rounded-lg bg-primary text-primary-foreground
                     text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              <span aria-hidden="true">+</span>
              New Project
            </Link>
          </div>
        )}

        {projectsQuery.isSuccess && recentProjects().length > 0 && (
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {recentProjects().map((project) => (
              <ProjectCard
                projectId={project.projectId}
                name={project.name}
                description={project.description}
                state={project.state}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DashboardPage() {
  return (
    <ErrorBoundary
      fallback={(err) => (
        <ErrorCard
          title="Dashboard error"
          message={err?.message ?? "An unexpected error occurred."}
        />
      )}
    >
      <Suspense fallback={<ProjectsSkeleton />}>
        <DashboardContent />
      </Suspense>
    </ErrorBoundary>
  );
}
